import { Button, ButtonGroup, Checkbox, Dialog, FormLabel, InputAdornment, OutlinedInput, TextareaAutosize } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { ArrowBack, Close } from '@material-ui/icons';
import tinycolor from 'tinycolor2';

import {
	convertInputTimeToSeconds,
	convertSecondsToInputTime,
	getDivEmbedCode,
	getIframeEmbedCode,
	getPreviewLink,
	joinClassNames,
} from '../../util/util';
import { setOpenSharingModal } from '../../actions/configurationActions';
import { Embedded, Email, Facebook, LinkedIn, X } from '../QPlayerSVGElement';

import ControlButton from './ControlButton';

import './ShareMediaDialog.scss';

interface ShareMediaDialogProps {
	configs?: any;
	mediaDetails?: any;
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
}
const baseClassName = 'qplayer__sharing-dialog';

const ShareMediaDialog = ({ sideWindowOpen, isMobile, playerConfigs }: ShareMediaDialogProps) => {
	const dispatch = useDispatch() as any;
	const { t } = useTranslation();

	const { mediaName, accountId, mediaType, openSharingModal, showCountdownModule, embedShareSettings, isMediaLive, videoHoverUrl } = useSelector(
		(state) => (state as any).configurationReducer
	);
	const { currentPosition } = useSelector((state) => (state as any).playerReducer);

	const [selectedEmbedded, setSelectedEmbedded] = useState<'div' | 'iframe' | null>(null);
	const [startTimeChecked, setStartTimeChecked] = useState(false);
	const [startTime, setStartTime] = useState(currentPosition);
	const [timeInputValue, setTimeInputValue] = useState('');
	const [isCheckboxHovering, setCheckboxHovering] = useState(false);

	const color = useMemo(() => playerConfigs?.playerStyling?.primaryColor ?? '#126cfc', [playerConfigs?.playerStyling?.primaryColor]);
	const primaryBGColor = useMemo(() => ({ backgroundColor: color }), [color]);
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const getEmbeddedValue = useCallback(() => {
		const paramConfigs = queryString.parse(window.location.search);
		const params = {
			mediaDetails: {
				id: paramConfigs.scenarioId ?? paramConfigs.mediaId ?? paramConfigs.playlistId,
				title: mediaName,
			},
			configs: { id: paramConfigs.configId, ...playerConfigs },
			paramConfigs,
			accountId: accountId,
			type: mediaType,
			time: startTimeChecked ? startTime : undefined,
		};
		return selectedEmbedded === 'div' ? getDivEmbedCode({ ...params }) : getIframeEmbedCode({ ...params });
	}, [accountId, mediaName, mediaType, playerConfigs, selectedEmbedded, startTime, startTimeChecked]);

	const getLink = () => {
		const paramConfigs = queryString.parse(window.location.search);
		const params = {
			mediaDetails: {
				id: paramConfigs.scenarioId ?? paramConfigs.mediaId ?? paramConfigs.playlistId,
				title: mediaName,
			},
			configs: { id: paramConfigs.configId, ...playerConfigs },
			paramConfigs,
			accountId: accountId,
			type: mediaType,
			time: startTimeChecked ? startTime : undefined,
			embedShareSettings: embedShareSettings,
		};
		return getPreviewLink({ ...params });
	};

	const shareToFb = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://web.facebook.com/sharer/sharer.php?u=${shareUrl}&display=popup`;
		window.open(windowToOpen, 'facebookShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const shareToX = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://twitter.com/intent/tweet?url=${shareUrl}`;
		window.open(windowToOpen, 'twtShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const shareToLinkedin = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://www.linkedin.com/shareArticle?url=${shareUrl}&mini=true`;
		window.open(windowToOpen, 'linkedinShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const mailTo = () => {
		const shareUrl = encodeURIComponent(getLink());
		window.open('mailto:?body=' + shareUrl);
	};

	const handleOnTimeCheckboxChange = (_event: React.ChangeEvent<{}>, checked: boolean) => {
		setStartTimeChecked(checked);
		checked && timeInputValue && setStartTime(convertInputTimeToSeconds(timeInputValue));
	};

	const handleCopyText = (textToCopy: string) => {
		navigator.clipboard.writeText(textToCopy);
	};

	const onTimeInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (isNaN(parseFloat(value))) {
			setStartTime(convertInputTimeToSeconds(value));
			return;
		}

		setTimeInputValue(value);
	}, []);

	const onSubmitStartTime = () => {
		const timeValue = startTimeChecked ? convertInputTimeToSeconds(timeInputValue) : '';
		setStartTime(timeValue);
	};

	const getTimeCheckboxLabel = () => {
		if (startTimeChecked) {
			return (
				<div
					style={{
						color: '#ffffffc7',
					}}
				>
					{t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')}
					<input
						className={`${baseClassName}__time-input`}
						onChange={onTimeInputChange}
						value={timeInputValue}
						onBlur={onSubmitStartTime}
					/>
				</div>
			);
		}
		return (
			<div
				style={{
					color: '#ffffffc7',
				}}
			>
				{t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')}
				{timeInputValue}
			</div>
		);
	};

	const closeShareModal = useCallback(() => {
		dispatch(setOpenSharingModal(false));
		setTimeInputValue('');
		setStartTimeChecked(false);
		setSelectedEmbedded(null);
		setStartTime(currentPosition);
	}, [currentPosition, dispatch]);

	useEffect(() => {
		timeInputValue === '' && openSharingModal && setTimeInputValue(convertSecondsToInputTime(currentPosition));
	}, [openSharingModal, currentPosition, timeInputValue]);

	return (
		openSharingModal && (
			<Dialog
				open={openSharingModal}
				className={joinClassNames(baseClassName)}
				style={{ zIndex: '99000', width: sideWindowOpen ? '75%' : '100%', fontSize: '12px' }}
				fullWidth={isMobile}
				onClose={(_event: any, reason: 'backdropClick' | 'escapeKeyDown') => {
					reason === 'escapeKeyDown' && closeShareModal();
				}}
			>
				<div className={`${baseClassName}__title qplayer-semibold-font`}>
					<ControlButton
						tabIndex={0}
						icon={<ArrowBack htmlColor="#ffffff" />}
						onClick={() => setSelectedEmbedded(null)}
						ariaLabel={t('SHARE_DIALOG_BACK')}
						tooltip={t('SHARE_DIALOG_BACK')}
						hoverStyle={hoverStyle}
						topControl
						aria-hidden={selectedEmbedded ? 'false' : 'true'}
						style={selectedEmbedded ? {} : { visibility: 'hidden' }}
						tooltipStyle={{ top: '40px' }}
					/>

					<span
						tabIndex={-1}
						ref={(r) => document.activeElement?.classList.contains('MuiDialog-scrollPaper') && r && r.focus()}
						aria-label={`${t('SHARE_DIALOG_TITLE')}. ${isMobile ? '' : t('SHARE_DIALOG')}`}
					>
						<span aria-hidden="true">{t('SHARE_DIALOG_TITLE')}</span>
					</span>

					<ControlButton
						tabIndex={0}
						icon={<Close htmlColor="#ffffff" />}
						onClick={closeShareModal}
						ariaLabel={t('SHARE_DIALOG_CLOSE')}
						tooltip={t('SHARE_DIALOG_CLOSE')}
						hoverStyle={hoverStyle}
						topControl
						tooltipStyle={{ top: '40px' }}
					/>
				</div>

				<div className={`${baseClassName}__content`}>
					{selectedEmbedded ? (
						<div
							className={`${baseClassName}__content-section ${baseClassName}__content-section-embedded ${baseClassName}__input`}
							style={{ display: 'flex', flexDirection: 'column-reverse', position: 'relative' }}
						>
							<div className={`${baseClassName}__content-action`}>
								<ButtonGroup>
									<Button
										role="tab"
										aria-selected={selectedEmbedded === 'div' ? 'true' : 'false'}
										aria-controls="tabpanel-div"
										className={joinClassNames(`${baseClassName}__tab`)}
										onClick={() => setSelectedEmbedded('div')}
										style={
											selectedEmbedded === 'div'
												? { borderColor: 'transparent', borderBottomColor: color }
												: { borderColor: 'transparent', borderBottomColor: 'rgba(255, 255, 255, 0.2)' }
										}
										aria-label={t('SHARE_DIALOG_DIV_EMBED')}
									>
										<span aria-hidden="true">div</span>
									</Button>
									<Button
										role="tab"
										aria-selected={selectedEmbedded === 'iframe' ? 'true' : 'false'}
										aria-controls="tabpanel-iframe"
										className={joinClassNames(`${baseClassName}__tab`)}
										onClick={() => setSelectedEmbedded('iframe')}
										style={
											selectedEmbedded === 'iframe'
												? { borderColor: 'transparent', borderBottomColor: color }
												: { borderColor: 'transparent', borderBottomColor: 'rgba(255, 255, 255, 0.2)' }
										}
										aria-label={t('SHARE_DIALOG_IFRAME_EMBED')}
									>
										<span aria-hidden="true">iframe</span>
									</Button>
								</ButtonGroup>
							</div>
							<TextareaAutosize
								role="tabpanel"
								id={selectedEmbedded === 'iframe' ? 'tabpanel-iframe' : 'tabpanel-div'}
								readOnly
								value={getEmbeddedValue()}
								aria-label={t('SHARE_DIALOG_SHARE_EMBED')}
							/>
							<Button
								variant="contained"
								color="primary"
								size="small"
								aria-label={t(selectedEmbedded === 'div' ? 'SHARE_DIALOG_COPY_EMBED_DIV' : 'SHARE_DIALOG_COPY_EMBED_IFRAME')}
								onClick={() => handleCopyText(getEmbeddedValue())}
								style={{
									...primaryBGColor,
									width: '52px',
									height: '26px',
									padding: '4px 10px',
									minWidth: '52px',
									position: 'absolute',
									right: '12px',
								}}
							>
								{t('LABEL_COPY_BUTTON')}
							</Button>
						</div>
					) : (
						<>
							<div
								className={`${baseClassName}__content-section ${baseClassName}__content-section--no-space`}
								style={{ display: 'flex' }}
							>
								<ControlButton
									tabIndex={0}
									icon={<Embedded />}
									onClick={() => setSelectedEmbedded('div')}
									ariaLabel={t('SHARE_DIALOG_OPEN_EMBED_ARIA_LABEL')}
									tooltip={t('SHARE_DIALOG_OPEN_EMBED')}
									hoverStyle={hoverStyle}
									topControl
									tooltipStyle={{ top: '-30px', left: '-15px' }}
								/>
								<ControlButton
									tabIndex={0}
									icon={<Facebook />}
									onClick={shareToFb}
									ariaLabel={t('SHARE_DIALOG_SHARE_TO_FACEBOOK_ARIA_LABEL')}
									tooltip={t('SHARE_DIALOG_SHARE_TO_FACEBOOK')}
									hoverStyle={hoverStyle}
									topControl
									tooltipStyle={{ top: '-30px' }}
								/>
								<ControlButton
									tabIndex={0}
									icon={<X />}
									onClick={shareToX}
									ariaLabel={t('SHARE_DIALOG_SHARE_TO_X_ARIA_LABEL')}
									tooltip={t('SHARE_DIALOG_SHARE_TO_X')}
									hoverStyle={hoverStyle}
									topControl
									tooltipStyle={{ top: '-30px' }}
								/>
								<ControlButton
									tabIndex={0}
									icon={<LinkedIn />}
									onClick={shareToLinkedin}
									ariaLabel={t('SHARE_DIALOG_SHARE_TO_LINKEDIN_ARIA_LABEL')}
									tooltip={t('SHARE_DIALOG_SHARE_TO_LINKEDIN')}
									hoverStyle={hoverStyle}
									topControl
									tooltipStyle={{ top: '-30px' }}
								/>
								<ControlButton
									tabIndex={0}
									icon={<Email />}
									onClick={mailTo}
									ariaLabel={t('SHARE_DIALOG_SHARE_TO_EMAIL_ARIA_LABEL')}
									tooltip={t('SHARE_DIALOG_SHARE_TO_EMAIL')}
									hoverStyle={hoverStyle}
									topControl
									tooltipStyle={{ top: '-30px' }}
								/>
							</div>

							<OutlinedInput
								className={`${baseClassName}__content-section ${baseClassName}__input`}
								endAdornment={
									<InputAdornment position="end">
										<Button
											variant="contained"
											color="primary"
											size="small"
											style={{ ...primaryBGColor, width: '52px', height: '26px', padding: '4px 10px', minWidth: '52px' }}
											aria-label={t('SHARE_DIALOG_COPY_LINK')}
											onClick={() => handleCopyText(getLink())}
										>
											{t('LABEL_COPY_BUTTON')}
										</Button>
									</InputAdornment>
								}
								value={getLink()}
								onChange={(_event) => {}}
								fullWidth
								readOnly
								aria-label={t('SHARE_DIALOG_SHARE_LINK')}
							/>
						</>
					)}

					{!showCountdownModule && mediaType === 'video' && !isMediaLive && videoHoverUrl && (
						<div className={`${baseClassName}__content-section ${baseClassName}__content-section--flex`}>
							<Checkbox
								inputProps={{
									'aria-label': startTimeChecked
										? t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL_DEACTIVATED')
										: t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL'),
								}}
								onChange={handleOnTimeCheckboxChange}
								checked={startTimeChecked}
								color={'primary'}
								style={
									isCheckboxHovering
										? { ...hoverStyle, color: '#ffffff', padding: '2px', marginRight: '2px' }
										: { color: '#ffffff', padding: '2px', marginRight: '2px' }
								}
								onMouseEnter={() => setCheckboxHovering(true)}
								onMouseLeave={() => setCheckboxHovering(false)}
								onFocus={() => setCheckboxHovering(true)}
								onBlur={() => setCheckboxHovering(false)}
							/>
							<div
								aria-hidden="true"
								className={joinClassNames([
									'controlbar__control-tooltip',
									'controlbar__control-tooltip--light',
									isCheckboxHovering ? 'controlbar__control-tooltip--visible' : '',
								])}
								style={{ top: '-30px', left: '-15px' }}
							>
								<div className="controlbar__control-tooltip-text">
									{startTimeChecked
										? t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL_DEACTIVATED')
										: t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL')}
								</div>
							</div>
							<FormLabel>{getTimeCheckboxLabel()}</FormLabel>
						</div>
					)}
				</div>
			</Dialog>
		)
	);
};

export default ShareMediaDialog;
